/**
 * KKASR 服務層入口（EVAS2.0）
 *
 * KKASR 提供 ASR 模型與 LLM 後端，與 kingcolon 是兩個獨立部署。
 * 這一層只負責「怎麼跟它說話」，不含任何 UI 邏輯與 Vue 相依。
 *
 * ── 兩條連線路徑，位址來源不同（很容易搞混，先講清楚）──
 *
 *   音訊上行：瀏覽器 --WebSocket--> KKASR
 *     由瀏覽器直接發起，所以位址必須是「瀏覽器連得到」的（localhost / 區網 IP），
 *     不能用 host.docker.internal——那是 container 之間的位址。
 *
 *   結果查詢：瀏覽器 --HTTP--> KKASR
 *     同上，也是瀏覽器直接打。
 *
 *   LLM 結果：KKASR --webhook--> kingcolon server
 *     這條不經過瀏覽器，位址在 server 端設定，與這裡無關。
 *
 * 位址優先由 `/api/setting` 的 runtime config 提供
 *（kkasr_http_url / kkasr_ws_url）。未設定時才依目前頁面的 hostname 與部署埠
 * 推導同一台主機上的 KKASR，避免從別台工作站開啟時誤連瀏覽器自己的 localhost。
 */

import { WSClient, WS_STATE } from './ws_client'
import { FileAudioSource } from './audio/audio_source_file'
import { MicAudioSource } from './audio/audio_source_mic'
import { CameraController } from './camera'

export {
  WSClient,
  WS_STATE,
  FileAudioSource,
  MicAudioSource,
  CameraController,
}

/** 無瀏覽器 location 時的保守開發預設。 */
const DEFAULT_HTTP_URL = 'http://localhost:5010'
const DEFAULT_WS_URL = 'ws://localhost:8765'

/**
 * 前端對外埠可辨識同機器上是哪一套 stack；hostname 始終沿用使用者正在看的主機。
 * 9531 是 -g stack，其他埠維持本機／既有開發預設，不碰 leo 或醫院明確設定。
 */
const STACK_PORTS_BY_CLIENT_PORT = {
  '9531': { http: '5011', ws: '8776' },
}
const DEFAULT_STACK_PORTS = { http: '5010', ws: '8765' }
const REMOTE_LEGACY_STACK_PORTS = { http: '5010', ws: '8775' }
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]'])

const trimmedUrl = (value) => (
  typeof value === 'string' && value.trim() ? value.trim().replace(/\/+$/, '') : null
)

function inferKkasrEndpoints(locationLike) {
  const currentLocation = locationLike || (
    typeof window !== 'undefined' && window.location ? window.location : null
  )
  if (!currentLocation || !currentLocation.hostname) {
    return { httpUrl: DEFAULT_HTTP_URL, wsUrl: DEFAULT_WS_URL }
  }

  const secure = currentLocation.protocol === 'https:'
  const clientPort = String(currentLocation.port || '')
  const hostname = String(currentLocation.hostname)
  const ports = STACK_PORTS_BY_CLIENT_PORT[clientPort]
    || (
      clientPort === '9530' && !LOOPBACK_HOSTS.has(hostname)
        ? REMOTE_LEGACY_STACK_PORTS
        : DEFAULT_STACK_PORTS
    )
  return {
    httpUrl: `${secure ? 'https' : 'http'}://${hostname}:${ports.http}`,
    wsUrl: `${secure ? 'wss' : 'ws'}://${hostname}:${ports.ws}`,
  }
}

/**
 * 「一直重複打、而且有人在等它讓路」那幾支的逾時（ms）。
 *
 * 只給**輪詢與搭便車的補償工作**用：`/refine_result`、`/commands`、`/atlas`、
 * `/key_info_terms`。它們都是「查一下就回」的等級（正常百毫秒內），10 秒是留給
 * KKASR 忙碌時的尖峰；而它們的呼叫端跑在每秒一次的輪詢裡、共用一個 in-flight
 * 旗標，一個不回話的請求就能讓整條輪詢停擺（逐字稿不更新、指令不消費）。
 *
 * ⚠️ **不要**把這個值套到一次性操作上（見 EOS_REQUEST_TIMEOUT_MS）。逾時策略是
 *    呼叫端的決定，不是傳輸層的決定：同一支 `_json` 服務的端點，合法耗時差了
 *    一個數量級。
 */
const POLL_REQUEST_TIMEOUT_MS = 10000

/**
 * `/eos` 的逾時（ms）。
 *
 * ⚠️ 這個數字是**對齊後端**來的，不是憑感覺：`KKASR/KKApp/ct2_asr_manager2.py`
 *    的 `eos(finalize=True, wait_timeout_sec=60.0, eos_delay_sec=1.0)` —— 最終解碼
 *    合法可以跑到 61 秒，`/eos` 這支 handler 就是在等它。套上輪詢那個 10 秒會讓
 *    **長一點的檢查按下結束時被判定成失敗**，而那是整個流程最不能出錯的一段。
 *    90 秒＝61 秒的後端上限再加一段餘裕。
 *
 * ⚠️ 後端那兩個預設值改了，這裡要跟著改。
 */
const EOS_REQUEST_TIMEOUT_MS = 90000

/**
 * 從 vuex 的 runtime setting 解析 KKASR 位址。
 * @param {object} setting store.getters.getSetting
 * @param {Location|object} [locationLike] 測試可注入；production 預設使用 window.location
 */
export function resolveKkasrEndpoints(setting, locationLike) {
  const s = setting || {}
  const fallback = inferKkasrEndpoints(locationLike)
  return {
    httpUrl: trimmedUrl(s.kkasr_http_url) || fallback.httpUrl,
    wsUrl: trimmedUrl(s.kkasr_ws_url) || fallback.wsUrl,
  }
}

/**
 * KKASR REST client。
 *
 * 取代舊 dev.html 裡 ui.js 的散裝 fetch：那邊每支函式各自組 URL、各自處理
 * session 比對，搬進 Vue 後改成一個物件持有 baseUrl 與 sessionId。
 */
export class KkasrApi {
  constructor(baseUrl) {
    this.baseUrl = (baseUrl || DEFAULT_HTTP_URL).replace(/\/+$/, '')
  }

  /**
   * 打一支 KKASR 的 REST 端點。
   *
   * ⚠️ 逾時是**呼叫端逐支決定**的（`timeoutMs`），這一層預設不設限。
   *
   *    理由是同一個 client 服務的端點合法耗時差了一個數量級：輪詢那幾支是
   *    「查一下就回」（百毫秒），但 `/eos` 要等最終解碼（後端上限 61 秒）、
   *    `/session/start` 會卡在與 `/eos` 同一把 `asr_transition_lock` 上（接管
   *    正在收尾的那一場時同樣可能等 60 秒）。在傳輸層寫死一個值，等於拿最短的
   *    那個需求去砍最長的那條路徑——實測後果是**長檢查按下結束會被誤判為失敗**。
   *
   *    要設的地方一定要設：`fetch` 預設會等到瀏覽器自己放棄（分鐘級），而 KKASR
   *    最常見的故障不是拒絕連線，是「接受連線但不回話」（模型載入中、GPU 卡住、
   *    container 半死）。逾時的失敗形狀與網路錯誤相同（throw），呼叫端既有的
   *    catch 就接得住，不必為它另開一條錯誤路徑。
   *
   * @param {string} path
   * @param {object} options fetch 的選項
   * @param {{timeoutMs?: number}} [config] timeoutMs 省略或 0 = 不設逾時
   */
  async _json(path, options, { timeoutMs = 0 } = {}) {
    const useTimeout = timeoutMs > 0 && typeof AbortController !== 'undefined'
    const controller = useTimeout ? new AbortController() : null
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null

    try {
      const res = await fetch(`${this.baseUrl}${path}`, {
        ...options,
        ...(controller ? { signal: controller.signal } : {}),
      })
      let body = null
      try {
        body = await res.json()
      } catch (e) {
        body = null
      }
      return { ok: res.ok, status: res.status, body }
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  /**
   * 建立收音 session。
   *
   * examinationId / accNo 會被 KKASR 原樣保管，並在 LLM 結果 webhook 推回
   * kingcolon 時帶上——沒有它們，server 收到報告不知道該寫進哪一筆檢查。
   *
   * force=false 時，若已有另一場檢查在收音會回 409（KKASR 的 ASR 只有一路）。
   * 呼叫端要把這個狀況顯示給使用者，而不是靜默接管別人的檢查。
   */
  startSession({
    sourceMode = 'mic',
    examinationId = null,
    accNo = null,
    force = false,
    // 預設 false 是保護非 EVAS 的既有呼叫端；正式 /exam2 會明確傳 true。
    // 若其他流程沒有 EVAS GO，誤開 crop 會得到空輸入，因此不能改成全域預設 true。
    evasCrop = false,
    // 正式 kingcolon session 要求報告在 DB-derived procedure type 綁定前 fail closed。
    // dev.html／舊呼叫端不帶時仍維持原本的 legacy report 行為。
    requireProcedureContext = false,
  }) {
    return this._json('/session/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_mode: sourceMode,
        examination_id: examinationId,
        acc_no: accNo,
        force,
        // evas_crop：送 LLM 前只取 `evas go` → 最終 `evas finish` 之間的逐字稿。
        // 正式 EVAS2 檢查明確開啟；被 continue 取消的 finish 不會提早截斷。
        evas_crop: evasCrop,
        require_procedure_context: requireProcedureContext,
      }),
    })
  }

  currentSession() {
    return this._json('/session/current', { method: 'GET' })
  }

  /**
   * 逐字稿（cursor 模式，非破壞性）。
   *
   * 帶 afterSeq 才是 cursor 語義——不帶會走 KKASR 的舊 pop 模式，讀過即消失，
   * 瀏覽器一重整那段逐字稿就永久遺失。這裡一律用 cursor。
   */
  refineSince(sessionId, afterSeq = 0, limit = 200) {
    const qs = `session_id=${encodeURIComponent(sessionId)}&after_seq=${afterSeq}&limit=${limit}`
    return this._json(`/refine_result?${qs}`, { method: 'GET' }, { timeoutMs: POLL_REQUEST_TIMEOUT_MS })
  }

  async finalTranscriptLatest(sessionId, examinationId) {
    const qs = `session_id=${encodeURIComponent(sessionId)}`
    const path = `/api/evas2-report/${encodeURIComponent(examinationId)}/final-transcript?${qs}`
    const useTimeout = typeof AbortController !== 'undefined'
    const controller = useTimeout ? new AbortController() : null
    const timer = controller ? setTimeout(() => controller.abort(), POLL_REQUEST_TIMEOUT_MS) : null
    try {
      const res = await fetch(path, {
        method: 'GET',
        credentials: 'same-origin',
        ...(controller ? { signal: controller.signal } : {}),
      })
      let body = null
      try { body = await res.json() } catch (_) { body = null }
      return { ok: res.ok, status: res.status, body }
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  /** 尚未 commit 的單一 FAST draft snapshot；不具 cursor，也不會落地。 */
  fastLatest(sessionId) {
    const qs = `session_id=${encodeURIComponent(sessionId)}`
    return this._json(`/fast_result?${qs}`, { method: 'GET' }, { timeoutMs: POLL_REQUEST_TIMEOUT_MS })
  }

  /** 語音指令事件（evas go / finish / cecum / cleansing / atlas），同樣是 cursor 模式 */
  commandsSince(sessionId, afterSeq = 0, limit = 200) {
    const qs = `session_id=${encodeURIComponent(sessionId)}&after_seq=${afterSeq}&limit=${limit}`
    return this._json(`/commands?${qs}`, { method: 'GET' }, { timeoutMs: POLL_REQUEST_TIMEOUT_MS })
  }

  /**
   * 病理圖鑑目錄（atlas_paris → 分類資訊 + images[] + 來源）。
   *
   * 與 session 無關，開場拉一次即可。images[].image_url 與相容欄位 image_url 都是
   * 相對路徑，要自己接上 baseUrl——KKASR 不知道瀏覽器如何連進來。
   */
  listAtlas() {
    return this._json('/atlas', { method: 'GET' }, { timeoutMs: POLL_REQUEST_TIMEOUT_MS })
  }

  /**
   * Key Info 詞表的分類（`{ lesion: [...], location: [...], ... }`）。
   *
   * 同 /atlas：與 session 無關，開場拉一次即可。截圖語音編輯需要 lesion 那一類，
   * 才分得出 refine 那串攤平的 keywords 裡哪個是病灶名。
   */
  listKeyInfoTerms() {
    return this._json('/key_info_terms', { method: 'GET' }, { timeoutMs: POLL_REQUEST_TIMEOUT_MS })
  }

  /** 把 /atlas 給的相對路徑補成瀏覽器載得到的絕對網址 */
  absoluteUrl(path) {
    return `${this.baseUrl}${path}`
  }

  /** 結束收音，觸發 LLM。結果由 KKASR webhook 推回 server，前端不必等。 */
  signalEos(sessionId, audioTarget = null) {
    const target = audioTarget && Number.isFinite(Number(audioTarget.targetSamples))
      ? {
          target_samples: Number(audioTarget.targetSamples),
          sample_rate: Number(audioTarget.sampleRate),
        }
      : {}
    return this._json('/eos', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, ...target }),
    }, { timeoutMs: EOS_REQUEST_TIMEOUT_MS })
  }

  /** 使用者中止收音（未 EOS）。KKASR 會捨棄該 session、不跑 LLM。 */
  streamingOff(sessionId) {
    return this._json('/session/streaming_off', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    })
  }

  /**
   * LLM 產出的即時查詢（Key Info 面板用）。
   *
   * ⚠️ 這兩支在 KKASR 是 current-session-only（見 app.py 的 get_visible_session_id），
   * 換了 session 就查不到舊的。要在檢查結束後才取結果，走 kingcolon server 讀
   * examination_transcripts（webhook 已落地），不要靠這裡。
   */
  llmReportLatest(sessionId) {
    const qs = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''
    return this._json(`/llm_report_latest${qs}`, { method: 'GET' })
  }

  llmExtractLatest(sessionId) {
    const qs = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ''
    return this._json(`/llm_extract_latest${qs}`, { method: 'GET' })
  }

  getAsrLanguage() {
    return this._json('/asr_language', { method: 'GET' })
  }

  setAsrLanguage(language) {
    return this._json('/asr_language', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language }),
    })
  }
}
